/**
 * Subscription Status Caching Utilities
 * Prevents redundant calls to checkAndUpdateFreeTrialStatus by caching results in session storage
 * Cache duration: 24 hours (trial status only needs to be checked once per day)
 */

import { tracedCallable } from "../utilities/tracedCallable";

// Debug logging - set to true to enable verbose logging
const DEBUG_LOGGING = false;
const debugLog = (...args) => DEBUG_LOGGING && console.log(...args);

const CACHE_KEY = "freeTrialStatusCache";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours (once per day)

/**
 * Get cached trial status for an organization
 * @param {string} orgId - Organization ID
 * @returns {Object|null} - Cached trial status data or null if not cached/expired
 */
export const getCachedTrialStatus = (orgId) => {
  try {
    const cached = sessionStorage.getItem(`${CACHE_KEY}_${orgId}`);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        debugLog(`📋 Using cached trial status for org: ${orgId}`);
        return data;
      } else {
        debugLog(`📋 Trial status cache expired for org: ${orgId}`);
        // Remove expired cache
        sessionStorage.removeItem(`${CACHE_KEY}_${orgId}`);
      }
    }
  } catch (error) {
    console.warn("Error reading trial status cache:", error);
    // Clear potentially corrupted cache
    try {
      sessionStorage.removeItem(`${CACHE_KEY}_${orgId}`);
    } catch (clearError) {
      console.warn("Error clearing corrupted cache:", clearError);
    }
  }
  return null;
};

/**
 * Cache trial status for an organization
 * @param {string} orgId - Organization ID
 * @param {Object} data - Trial status data to cache
 */
export const setCachedTrialStatus = (orgId, data) => {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(`${CACHE_KEY}_${orgId}`, JSON.stringify(cacheEntry));
    debugLog(`📋 Cached trial status for org: ${orgId}`, data);
  } catch (error) {
    console.warn("Error setting trial status cache:", error);
    // Session storage might be full - try to clear old entries
    clearOldCacheEntries();
  }
};

/**
 * Clear old/expired cache entries to free up space
 */
const clearOldCacheEntries = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_KEY)) {
        try {
          const cached = sessionStorage.getItem(key);
          if (cached) {
            const { timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp >= CACHE_DURATION) {
              keysToRemove.push(key);
            }
          }
        } catch (parseError) {
          // Invalid entry, mark for removal
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
    });

    if (keysToRemove.length > 0) {
      debugLog(
        `📋 Cleared ${keysToRemove.length} expired trial status cache entries`,
      );
    }
  } catch (error) {
    console.warn("Error clearing old cache entries:", error);
  }
};

/**
 * Check trial status with caching - main function to use instead of direct calls
 * @param {string} orgId - Organization ID
 * @param {Object} authHook - Firebase auth hook with functions instance
 * @returns {Promise<Object>} - Trial status data
 */
export const checkTrialStatusWithCache = async (orgId, authHook) => {
  // Check cache first
  const cached = getCachedTrialStatus(orgId);
  if (cached) {
    return cached;
  }

  debugLog(
    `📋 Cache miss - calling checkAndUpdateFreeTrialStatus for org: ${orgId}`,
  );

  try {
    // Call function if not cached
    const checkAndUpdateFreeTrialStatus = tracedCallable(
      authHook.functions,
      "checkAndUpdateFreeTrialStatus",
    );

    const result = await checkAndUpdateFreeTrialStatus({ orgId });

    // Cache the result
    setCachedTrialStatus(orgId, result.data);

    return result.data;
  } catch (error) {
    console.error("Error checking free trial status:", error);
    throw error; // Re-throw to let calling code handle it
  }
};

/**
 * Clear cache for a specific organization (useful for testing or when status changes)
 * @param {string} orgId - Organization ID
 */
export const clearTrialStatusCache = (orgId) => {
  try {
    sessionStorage.removeItem(`${CACHE_KEY}_${orgId}`);
    debugLog(`📋 Cleared trial status cache for org: ${orgId}`);
  } catch (error) {
    console.warn("Error clearing trial status cache:", error);
  }
};

/**
 * Clear all trial status caches (useful for logout or troubleshooting)
 */
export const clearAllTrialStatusCaches = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_KEY)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
    });

    debugLog(
      `📋 Cleared all trial status caches (${keysToRemove.length} entries)`,
    );
  } catch (error) {
    console.warn("Error clearing all trial status caches:", error);
  }
};

/**
 * Get cache statistics for debugging
 * @returns {Object} - Cache statistics
 */
export const getTrialStatusCacheStats = () => {
  try {
    const stats = {
      totalEntries: 0,
      validEntries: 0,
      expiredEntries: 0,
      orgs: [],
    };

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_KEY)) {
        stats.totalEntries++;
        const orgId = key.replace(`${CACHE_KEY}_`, "");

        try {
          const cached = sessionStorage.getItem(key);
          if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            const isExpired = Date.now() - timestamp >= CACHE_DURATION;

            if (isExpired) {
              stats.expiredEntries++;
            } else {
              stats.validEntries++;
            }

            stats.orgs.push({
              orgId,
              cached: true,
              expired: isExpired,
              freeTrial: data?.freeTrial,
              subscriptionCurrent: data?.subscriptionCurrent,
              cachedAt: new Date(timestamp).toISOString(),
            });
          }
        } catch (parseError) {
          stats.expiredEntries++; // Count unparseable as expired
        }
      }
    }

    return stats;
  } catch (error) {
    console.warn("Error getting cache stats:", error);
    return { error: error.message };
  }
};
